/**
 * Pantalla 1 (docs/FASE-1.md §6): email, contraseña y campo TOTP cuando el API lo pide.
 *
 * El API no distingue con un `code` propio "credenciales inválidas" de "falta el TOTP" (ambos
 * casos son `unauthenticated`, ver `apps/idp/src/platform/operators.ts`); la única señal es el
 * texto del mensaje. Por eso, tras un primer intento fallido cuyo mensaje menciona "TOTP", la
 * consola revela el campo y deja reintentar sin perder lo ya escrito. Es una heurística basada en
 * texto (frágil si el mensaje cambia de redacción); lo ideal sería que el IdP expusiera un código
 * de error distinto para "se requiere TOTP" — ver informe final del agente `consola`.
 */
import { useState, type FormEvent, type ReactElement } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { login, useSession } from "../api/session";
import { Field } from "../components/Field";
import { toDisplayMessage } from "../lib/errors";

export function Login(): ReactElement {
  const { status } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") {
    const redirectTo = (location.state as { from?: string } | null)?.from ?? "/organizations";
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login({ email, password, ...(needsTotp && totp ? { totp } : {}) });
      navigate("/organizations", { replace: true });
    } catch (err) {
      const message = toDisplayMessage(err);
      setError(message);
      if (!needsTotp && /totp/i.test(message)) {
        setNeedsTotp(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <h1>Consola Continnua</h1>
        <p className="login-subtitle">Acceso de operadores</p>

        <Field label="Correo electrónico">
          {(props) => (
            <input
              {...props}
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <Field label="Contraseña">
          {(props) => (
            <input
              {...props}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>

        {needsTotp && (
          <Field label="Código de autenticador (TOTP)" hint="6 dígitos de tu aplicación autenticadora.">
            {(props) => (
              <input
                {...props}
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                autoComplete="one-time-code"
                maxLength={6}
                value={totp}
                onChange={(event) => setTotp(event.target.value.replace(/\D/g, ""))}
              />
            )}
          </Field>
        )}

        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="button-primary button-block" disabled={submitting}>
          {submitting ? "Ingresando…" : "Ingresar"}
        </button>

        <Link className="login-forgot-link" to="/forgot-password">
          ¿Olvidaste tu contraseña?
        </Link>
      </form>
    </div>
  );
}
